import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    FileText,
    Download,
    Copy,
    Mail,
    CheckCircle,
    Loader2,
    X
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * MeetingSummaryModal - Post-meeting AI-generated summary display
 * Shows key topics, decisions, action items, and notable quotes
 */
const MeetingSummaryModal = ({
    isOpen,
    onClose,
    summary,
    isLoading,
    onRegenerate,
    roomName
}) => {
    // Copy summary to clipboard
    const copySummary = () => {
        if (summary?.summary) {
            navigator.clipboard.writeText(summary.summary);
            toast.success('Summary copied to clipboard');
        }
    };

    // Download summary as markdown file
    const downloadSummary = () => {
        if (!summary?.summary) return;

        const content = `# Meeting Summary: ${roomName || 'Meeting'}\n\nGenerated: ${new Date().toLocaleString()}\n\n${summary.summary}`;
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `meeting-summary-${new Date().toISOString().split('T')[0]}.md`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Summary downloaded');
    };

    // Share via email
    const shareViaEmail = () => {
        if (!summary?.summary) return;

        const subject = encodeURIComponent(`Meeting Summary: ${roomName || 'Meeting'}`);
        const body = encodeURIComponent(summary.summary);
        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="bg-void-paper border border-white/10 max-w-2xl max-h-[80vh] flex flex-col">
                <DialogHeader className="border-b border-white/5 pb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-neon-cyan to-neon-purple flex items-center justify-center">
                                <FileText className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <DialogTitle className="text-white font-heading">Meeting Summary</DialogTitle>
                                <p className="text-xs text-grey-400 mt-0.5">
                                    AI-generated summary powered by Kautilya AI
                                </p>
                            </div>
                        </div>
                    </div>
                </DialogHeader>

                <ScrollArea className="flex-1 py-4">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-10 h-10 text-neon-cyan animate-spin mb-4" />
                            <p className="text-grey-300 font-medium">Generating summary...</p>
                            <p className="text-grey-500 text-sm mt-1">Analyzing meeting transcript</p>
                        </div>
                    ) : summary?.summary ? (
                        <div className="prose prose-invert prose-sm max-w-none">
                            {/* Success indicator */}
                            <div className="flex items-center gap-2 mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-400" />
                                <span className="text-green-300 text-sm">Summary generated successfully</span>
                            </div>

                            {/* Summary content rendered as markdown-like text */}
                            <div className="text-grey-200 whitespace-pre-wrap leading-relaxed">
                                {summary.summary.split('\n').map((line, index) => {
                                    // Style headers
                                    if (line.startsWith('## ') || line.startsWith('**')) {
                                        return (
                                            <p key={index} className="text-white font-semibold mt-4 mb-2 text-base">
                                                {line.replace(/^##\s*/, '').replace(/\*\*/g, '')}
                                            </p>
                                        );
                                    }
                                    // Style bullet points
                                    if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
                                        return (
                                            <p key={index} className="ml-4 mb-1 text-grey-300">
                                                {line}
                                            </p>
                                        );
                                    }
                                    // Regular text
                                    return line.trim() ? (
                                        <p key={index} className="mb-2 text-grey-300">
                                            {line}
                                        </p>
                                    ) : (
                                        <br key={index} />
                                    );
                                })}
                            </div>

                            {/* Metadata */}
                            <div className="mt-6 pt-4 border-t border-white/5 text-xs text-grey-500">
                                {summary.generated_at && (
                                    <p>Generated at: {new Date(summary.generated_at).toLocaleString()}</p>
                                )}
                                {typeof summary.transcript_length === 'number' && (
                                    <p>Transcript length: {summary.transcript_length} characters</p>
                                )}
                                {summary.model && <p>Model: {summary.model}</p>}
                            </div>
                        </div>
                    ) : summary?.error ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
                                <X className="w-8 h-8 text-red-400" />
                            </div>
                            <p className="text-red-400 font-medium mb-2">No summary available</p>
                            <p className="text-grey-500 text-sm mb-4">{summary.error}</p>
                            {onRegenerate && (
                                <Button
                                    onClick={onRegenerate}
                                    variant="outline"
                                    className="border-neon-cyan text-neon-cyan hover:bg-neon-cyan/10"
                                >
                                    Try Again
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <FileText className="w-12 h-12 mx-auto mb-4 text-grey-500 opacity-50" />
                            <p className="text-grey-400">No summary available yet</p>
                            <p className="text-grey-500 text-sm mt-1">
                                Make sure transcription was active during the meeting
                            </p>
                        </div>
                    )}
                </ScrollArea>

                {/* Actions */}
                {summary?.summary && (
                    <div className="border-t border-white/5 pt-4 flex flex-wrap gap-2">
                        <Button
                            onClick={copySummary}
                            variant="outline"
                            size="sm"
                            className="flex-1 border-white/10 text-grey-300 hover:bg-white/5"
                        >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy
                        </Button>
                        <Button
                            onClick={downloadSummary}
                            variant="outline"
                            size="sm"
                            className="flex-1 border-white/10 text-grey-300 hover:bg-white/5"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                        </Button>
                        <Button
                            onClick={shareViaEmail}
                            variant="outline"
                            size="sm"
                            className="flex-1 border-white/10 text-grey-300 hover:bg-white/5"
                        >
                            <Mail className="w-4 h-4 mr-2" />
                            Email
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default MeetingSummaryModal;
